import { forwardRef } from 'react';

export interface NewInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  invalid?: boolean;
  label?: string;
  /** When true (and `label` is set), renders a red `*` after the label to mark the field as mandatory. */
  required?: boolean;
  /** When true (and `label` is set), renders a green `*` marking the field as DPP-required. Mirrors `NewSelectMultiSelect`. */
  dppRequired?: boolean;
  /** Rendered inside the input's border on the LEFT of the text. Any node — string, glyph, icon-button, toggle switch, `<NewCheckbox />`, or a Fragment for multiple. */
  startAdornment?: React.ReactNode;
  /** Same as `startAdornment` but rendered on the RIGHT. Use `.bombiix-input__counter` for a `nn/mmm` character counter. */
  endAdornment?: React.ReactNode;
  /** Applies the blue-outline highlight skin (Figma bulk-update visual). */
  isHighlight?: boolean;
}

/**
 * NewInput — Bombiix master-design text input (Figma spec node 4734:158042).
 * Forwards refs so consumers can focus / measure.
 * Pass `invalid` for red border + trailing alert icon, `label` for a caption
 * above the field, and `startAdornment` / `endAdornment` to embed a helper
 * element (icon button, currency glyph, toggle switch, checkbox, character
 * counter, …) inside the input's own border.
 * Styling lives in `assets/scss/master-design/_input.scss` — no per-component CSS.
 */
const NewInput = forwardRef<HTMLInputElement, NewInputProps>(function NewInput(
  { className, invalid, label, required, dppRequired, type = 'text', startAdornment, endAdornment, isHighlight, ...rest },
  ref,
) {
  const hasStart = startAdornment != null && startAdornment !== false;
  const hasEnd = endAdornment != null && endAdornment !== false;
  const inputClass = ['bombiix-input', invalid ? 'bombiix-input--invalid' : '', hasStart || hasEnd ? 'bombiix-input--in-group' : '', className]
    .filter(Boolean)
    .join(' ');
  const fieldClass = [
    'bombiix-input__field',
    hasStart ? 'bombiix-input__field--has-start' : '',
    hasEnd ? 'bombiix-input__field--has-end' : '',
    invalid ? 'bombiix-input__field--invalid' : '',
  ]
    .filter(Boolean)
    .join(' ');
  const wrapClass = ['bombiix-input-wrap', isHighlight ? 'bombiix-input-wrap--bulk' : ''].filter(Boolean).join(' ');
  return (
    <div className={wrapClass}>
      {label ? (
        <span className="bombiix-input__label">
          {label}
          {required || dppRequired ? (
            <span className={`bombiix-input__required${dppRequired ? ' bombiix-input__required--green' : ''}`} aria-hidden="true">
              *
            </span>
          ) : null}
        </span>
      ) : null}
      <div className={fieldClass}>
        {hasStart ? <span className="bombiix-input__adornment bombiix-input__adornment--start">{startAdornment}</span> : null}
        <input ref={ref} type={type} className={inputClass} required={required} {...rest} />
        {hasEnd ? <span className="bombiix-input__adornment bombiix-input__adornment--end">{endAdornment}</span> : null}
        {invalid ? (
          <span className="bombiix-input__alert-icon" aria-hidden="true">
            <i className="bombiix-icon-validation f-24" />
          </span>
        ) : null}
      </div>
    </div>
  );
});

export default NewInput;
